import { ApolloError } from '@apollo/client';
import { Add, ContentCopy } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useCommunityContext } from 'providers';
import { Fragment, useMemo, useState } from 'react';

import { RoleName, SystemRoleName } from '@jebel/constants';

import { DividerOr } from 'shared/components/symbols';
import { Form, FormFieldArray } from 'shared/components/ui';
import { ROUTES } from 'shared/constants';
import { SendSchoolMemberInviteInput } from 'shared/graphql';
import {
  useResponsive,
  UserInvitationParsed,
  useToast,
  useUserInvitation,
  useUserInvitationCSV,
  useUpdateUserRoleAddons,
} from 'shared/hooks';
import { recordError } from 'shared/utils/record';

import { InviteMemberItem } from './InviteMemberItem';
import { FileInputButton } from './StyledComponents';
import type { InviteMemberFormValues } from './types';

const FILE_TYPE = '.csv';

const INVITING_CSV_MESSAGE = `Inviting imported users from CSV, please wait until the process finishes.`;
const INVITING_CSV_MESSAGE_KEY = `INVITING_CSV`;
const ERROR_GENERIC_MESSAGE = 'Something goes wrong when creating the user, please try again.';

const createInitialValues = (): UserInvitationParsed => ({
  firstname: '',
  lastname: '',
  email: '',
  phoneNumber: '',
  affiliation: '',
  affiliationDescription: '',
  graduatingYear: '',
});

const mapGraduatingYear = (year?: number | string): string | null => {
  if (!year) return null;
  const date = typeof year === 'string' ? DateTime.fromISO(year) : DateTime.utc(year, 1, 1);
  if (!date.isValid) return null;
  return date.toISODate();
};

const initialValues: InviteMemberFormValues = {
  members: [createInitialValues()],
};

/**
 * Modal props
 */
type InviteMemberMeta = {
  businessId: string;
};

interface Props {
  open: boolean;

  onClose: () => void;

  onSuccessfulSubmit?: () => void;

  isAdminInviting?: boolean;

  meta?: InviteMemberMeta;

  type?: RoleName | SystemRoleName;
}

const INVITATION_SENT_MESSAGE = 'Your invitations have been sent.';
const INVITATION_WAITING_MESSAGE = 'Your invitations have been sent and are being processed.';

export function InviteMemberModal({
  open,
  onClose,
  onSuccessfulSubmit,
  isAdminInviting = false,
  type = SystemRoleName.CommunityMember,
  meta,
}: Props) {
  const [uploading, setUploading] = useState(false);

  const { selectedCommunity: community } = useCommunityContext();
  const { isMobile } = useResponsive();
  const { showError, showSuccess, showWarning, showMessage, dismiss } = useToast();
  const { isLoading: isRolesLoading } = useUpdateUserRoleAddons();

  const showUploadCSV = !isMobile && isAdminInviting;

  const { inviteUsers, inviteAdmins, inviteBusinessAdmins, inviteBusinessManagers } =
    useUserInvitation({
      refetchQueries: ['SchoolMembersReport', 'MemberStats'],
      awaitRefetchQueries: true,
    });

  const { handle: handleUploadCSV } = useUserInvitationCSV({
    onStart() {
      setUploading(true);
      showMessage(INVITING_CSV_MESSAGE, { id: INVITING_CSV_MESSAGE_KEY });
    },

    async onComplete(parsed) {
      await onSubmit({ members: parsed });
      setUploading(false);
    },
  });

  const communityURL = useMemo(() => {
    const url = new URL(ROUTES.public.request, process.env.REACT_APP_COMMUNITY_DOMAIN);

    if (community?.id) {
      url.searchParams.set('community', community.id);
    }

    return url.toString();
  }, [community]);

  const copyCommunityURL = () => {
    navigator.clipboard.writeText(communityURL);
    showSuccess('Community URL copied to clipboard');
  };

  const onSubmit = async ({ members: formData }: InviteMemberFormValues) => {
    const userData = formData.map<SendSchoolMemberInviteInput>(data => ({
      email: data.email,
      firstname: data.firstname,
      lastname: data.lastname,
      phoneNumber: data.phoneNumber,
      affiliation: data.affiliation,
      affiliationDescription: data.affiliationDescription,
      graduatingYear: mapGraduatingYear(data.graduatingYear),
    }));

    try {
      if (userData.length === 0) {
        throw new Error('You haven`t provided needed info to invite person. Review it carefully.');
      }

      if (type === SystemRoleName.BusinessAdmin) {
        await inviteBusinessAdmins(userData);
      }

      if (type === SystemRoleName.BusinessManager) {
        if (!meta?.businessId) {
          return;
        }

        await inviteBusinessManagers(userData, meta?.businessId);
      }

      if (type === SystemRoleName.CommunityAdmin) {
        await inviteAdmins(userData);
      }

      if (type === SystemRoleName.CommunityMember) {
        await inviteUsers(userData, isAdminInviting);
      }

      if (userData.length < 10) {
        showSuccess(INVITATION_SENT_MESSAGE);
      } else {
        showWarning(INVITATION_WAITING_MESSAGE);
      }

      onSuccessfulSubmit?.();
      onClose();
    } catch (err) {
      recordError(err);

      if (err instanceof ApolloError) {
        for (const error of err.graphQLErrors) {
          showError(error.message);
        }

        return;
      }

      if (err instanceof Error) {
        showError(err.message);
        return;
      }

      showError(ERROR_GENERIC_MESSAGE);
    } finally {
      dismiss(INVITING_CSV_MESSAGE_KEY);
      setUploading(false);
    }
  };

  return (
    <Dialog maxWidth="sm" open={open} onClose={onClose} fullWidth>
      <DialogTitle>Invite Members</DialogTitle>

      <Form<InviteMemberFormValues> initialValues={initialValues} onSubmit={onSubmit}>
        {form => {
          const isPending = form.isSubmitting || form.isValidating || uploading || isRolesLoading;

          return (
            <Fragment>
              <DialogContent dividers>
                <Box display="flex" flexDirection="column" gap={4}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Typography variant="body2" color="textSecondary">
                      Share the invitation link below with others to invite them:
                    </Typography>

                    <Button
                      type="button"
                      variant="contained"
                      disabled={isPending}
                      disableElevation
                      startIcon={<ContentCopy />}
                      onClick={copyCommunityURL}
                    >
                      Copy sharable link
                    </Button>
                  </Box>

                  <DividerOr />

                  <FormFieldArray name="members">
                    {({ remove, push }) => (
                      <Fragment>
                        {form.values.members.map((_, index) => (
                          <InviteMemberItem
                            name="members"
                            key={index}
                            index={index}
                            onRemove={() => remove(index)}
                          />
                        ))}

                        <Button
                          startIcon={<Add />}
                          onClick={() => push(createInitialValues())}
                          size="small"
                          fullWidth
                        >
                          Add another
                        </Button>
                      </Fragment>
                    )}
                  </FormFieldArray>
                </Box>
              </DialogContent>

              <DialogActions>
                {showUploadCSV && (
                  <FileInputButton
                    accept={FILE_TYPE}
                    disabled={isPending}
                    onChange={handleUploadCSV}
                  >
                    Upload Csv
                  </FileInputButton>
                )}

                <Button onClick={onClose} disabled={isPending}>
                  Cancel
                </Button>

                <Button disabled={isPending} variant="contained" type="submit" color="primary">
                  {uploading
                    ? 'Uploading CSV...'
                    : form.isSubmitting
                      ? 'Sending Invites...'
                      : 'Send Invites'}
                </Button>
              </DialogActions>
            </Fragment>
          );
        }}
      </Form>
    </Dialog>
  );
}
